// Event-firing snippet for Unreal / C++. Companion subsystem to
// UPlayerBasePairing - signs each request with HMAC-SHA256 and is a
// no-op until the player has paired.

export function buildEventUnrealSnippet(apiKey: string, functionsBaseUrl: string): string {
  return `// Source/<YourModule>/PlayerBaseEvents.h + .cpp
// Add to the same module as UPlayerBasePairing. Build.cs deps:
//
//   PrivateDependencyModuleNames.AddRange(new[] { "HTTP", "Json", "OpenSSL" });
//
// Call from anywhere in your game:
//
//   UPlayerBaseEvents::Get(this)->Fire(TEXT("tutorialComplete"));
//
//   TSharedRef<FJsonObject> Payload = MakeShared<FJsonObject>();
//   Payload->SetStringField(TEXT("boss"), TEXT("stage1"));
//   Payload->SetNumberField(TEXT("tries"), 4);
//   UPlayerBaseEvents::Get(this)->FireWithPayload(TEXT("bossDefeated"), Payload);
//
// Behaviour:
//   * No-op until the player has paired, so it's safe to call from
//     gameplay code without guarding.
//   * Generates a fresh idempotencyKey per fire, so retries collapse
//     server-side instead of double-counting.
//   * 200 covers ok / duplicate / no_active_playtest - all expected
//     during normal operation. Anything else logs a UE_LOG warning so
//     config drift in dev builds is visible without crashing gameplay.

// --- PlayerBaseEvents.h -----------------------------------------------------
#pragma once

#include "CoreMinimal.h"
#include "Subsystems/GameInstanceSubsystem.h"
#include "Dom/JsonObject.h"
#include "PlayerBaseEvents.generated.h"

UCLASS()
class UPlayerBaseEvents : public UGameInstanceSubsystem
{
    GENERATED_BODY()
public:
    UFUNCTION(BlueprintCallable, Category = "PlayerBase")
    void Fire(const FString& EventName);

    void FireWithPayload(const FString& EventName, const TSharedRef<FJsonObject>& Payload);

    static UPlayerBaseEvents* Get(const UObject* WorldContext);
};

// --- PlayerBaseEvents.cpp ---------------------------------------------------
#include "PlayerBaseEvents.h"
#include "PlayerBasePairing.h"

#include "HttpModule.h"
#include "Interfaces/IHttpRequest.h"
#include "Interfaces/IHttpResponse.h"
#include "Misc/Guid.h"
#include "Misc/DateTime.h"
#include "Serialization/JsonReader.h"
#include "Serialization/JsonSerializer.h"

THIRD_PARTY_INCLUDES_START
#include <openssl/hmac.h>
THIRD_PARTY_INCLUDES_END

namespace
{
    static const FString ApiKey = TEXT("${apiKey}");
    static const FString EventsUrl = TEXT("${functionsBaseUrl}/events");

    FString HmacSha256Hex(const FString& Key, const FString& Body)
    {
        const FTCHARToUTF8 KeyUtf8(*Key);
        const FTCHARToUTF8 BodyUtf8(*Body);
        unsigned char Out[32];
        unsigned int OutLen = 0;
        HMAC(EVP_sha256(),
             KeyUtf8.Get(), KeyUtf8.Length(),
             reinterpret_cast<const unsigned char*>(BodyUtf8.Get()), BodyUtf8.Length(),
             Out, &OutLen);
        FString Hex;
        Hex.Reserve(OutLen * 2);
        const TCHAR* Digits = TEXT("0123456789abcdef");
        for (unsigned int i = 0; i < OutLen; ++i)
        {
            Hex.AppendChar(Digits[Out[i] >> 4]);
            Hex.AppendChar(Digits[Out[i] & 0xF]);
        }
        return Hex;
    }
}

UPlayerBaseEvents* UPlayerBaseEvents::Get(const UObject* WorldContext)
{
    if (!WorldContext) return nullptr;
    const UWorld* World = WorldContext->GetWorld();
    if (!World) return nullptr;
    return World->GetGameInstance()->GetSubsystem<UPlayerBaseEvents>();
}

void UPlayerBaseEvents::Fire(const FString& EventName)
{
    FireWithPayload(EventName, MakeShared<FJsonObject>());
}

void UPlayerBaseEvents::FireWithPayload(const FString& EventName, const TSharedRef<FJsonObject>& Payload)
{
    UPlayerBasePairing* Pairing = UPlayerBasePairing::Get(this);
    if (!Pairing || !Pairing->IsPaired()) return;

    const FString IdempotencyKey = FGuid::NewGuid().ToString(EGuidFormats::DigitsWithHyphensLower);
    const FString OccurredAt = FDateTime::UtcNow().ToIso8601();

    const TSharedRef<FJsonObject> Json = MakeShared<FJsonObject>();
    Json->SetStringField(TEXT("playerToken"), Pairing->GetPlayerToken());
    Json->SetStringField(TEXT("eventName"), EventName);
    Json->SetStringField(TEXT("idempotencyKey"), IdempotencyKey);
    Json->SetObjectField(TEXT("payload"), Payload);
    Json->SetStringField(TEXT("occurredAt"), OccurredAt);
    FString Body;
    const TSharedRef<TJsonWriter<>> Writer = TJsonWriterFactory<>::Create(&Body);
    FJsonSerializer::Serialize(Json, Writer);

    const FString Signature = HmacSha256Hex(ApiKey, Body);

    TSharedRef<IHttpRequest, ESPMode::ThreadSafe> Request = FHttpModule::Get().CreateRequest();
    Request->SetURL(EventsUrl);
    Request->SetVerb(TEXT("POST"));
    Request->SetHeader(TEXT("content-type"), TEXT("application/json"));
    Request->SetHeader(TEXT("x-playerbase-signature"), Signature);
    Request->SetContentAsString(Body);

    const FString EventNameCopy = EventName;
    Request->OnProcessRequestComplete().BindLambda(
        [EventNameCopy](FHttpRequestPtr, FHttpResponsePtr Response, bool bSuccess)
        {
            // 200 covers ok / duplicate / no_active_playtest - all expected.
            // Warn on anything else so config drift in dev builds is visible.
            if (!bSuccess || !Response.IsValid() || Response->GetResponseCode() != 200)
            {
                const int32 Code = Response.IsValid() ? Response->GetResponseCode() : 0;
                UE_LOG(LogTemp, Warning, TEXT("PlayerBase: %s returned %d"), *EventNameCopy, Code);
            }
        });
    Request->ProcessRequest();
}
`;
}
